import { useContext, useEffect, useMemo, useSyncExternalStore } from 'react'
import { ThemeContext } from '@/themes/ThemeContext'
import { createThemeController } from '@/themes/controller'
import { getTheme, STYLE_SENTINEL } from '@/themes/registry'
import { buildPickerModel } from '@/themes/picker'
import { toastInfo } from '@/components/ui/Toast'

// One controller per page load: the root attributes belong to the document, not to a component,
// and React StrictMode must not read storage (or log a reset) twice.
let shared = null
function getController() {
  if (!shared) {
    const storage = (() => { try { return window.localStorage } catch { return null } })()
    shared = createThemeController({
      storage,
      root: document.documentElement,
      matchMedia: typeof window.matchMedia === 'function' ? window.matchMedia.bind(window) : null,
      stylesApplied: () => getComputedStyle(document.documentElement).getPropertyValue(STYLE_SENTINEL).trim() !== '',
    })
  }
  return shared
}

export function ThemeProvider({ children }) {
  const controller = getController()
  const state = useSyncExternalStore(controller.subscribe, controller.getState)

  useEffect(() => controller.start(), [controller])

  useEffect(() => {
    // Read the live value: StrictMode re-runs this effect with the same closure, and the notice must show once.
    const notice = controller.getState().notice
    if (!notice) return
    controller.dismissNotice()
    toastInfo('Theme reset', notice)
  }, [state.notice, controller])

  const value = useMemo(() => ({
    // NAMING ODDITY, kept so no call site changes: `theme` is the resolved APPEARANCE, 'light' or 'dark'
    // (it was the only theme setting before named themes). The theme's own id is `themeId`.
    // `setTheme` and `toggleTheme` also act on the appearance. `setTheme` takes light, dark or system.
    theme: state.appearance,
    resolvedTheme: state.appearance,
    setTheme: controller.setAppearanceSetting,
    toggleTheme: controller.toggleAppearance,
    themeId: state.themeId,
    setThemeId: controller.setThemeId,
    appearanceSetting: state.appearanceSetting,
    setAppearanceSetting: controller.setAppearanceSetting,
    capabilities: getTheme(state.themeId).capabilities,
    picker: buildPickerModel({ themeId: state.themeId, appearanceSetting: state.appearanceSetting }),
  }), [state, controller])

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>
}

export function useTheme() {
  const context = useContext(ThemeContext)
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider')
  }
  return context
}
